/* usePreviewQuota — server-side preview view counter + lock signal.

   On mount: POSTs /api/engagement/preview-view {preview_id, kind} for logged-in
   users (silently skipped for anonymous). The backend de-dupes per month, so
   re-visiting the same role doesn't burn extra views.

   Exposes a `lxt:quota` event other components can listen to so QuotaBanner
   can update without a refetch.

   Returns:
     { loading, plan, used, quota, remaining, locked }
*/
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

let _last = null;          // shared cache so QuotaBanner can read latest

const broadcast = (payload) => {
  _last = payload;
  try { window.dispatchEvent(new CustomEvent('lxt:quota', { detail: payload })); } catch (_) { /* noop */ }
};

export const getLastQuota = () => _last;

export const usePreviewQuota = (previewId, kind = 'role') => {
  const [state, setState] = useState(() => _last || { loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!previewId) return;
      try {
        const { data } = await axios.post(`${API}/engagement/preview-view`, { preview_id: previewId, kind });
        if (cancelled) return;
        const payload = { loading: false, ...data, used: data.views_this_month };
        setState(payload);
        broadcast(payload);
      } catch (err) {
        if (cancelled) return;
        // Anonymous users: silently ignore (no enforcement until they sign in)
        const status = err?.response?.status;
        if (status === 401) {
          const payload = { loading: false, plan: 'anonymous', used: 0, quota: 0, remaining: 0, locked: false };
          setState(payload);
          broadcast(payload);
        } else {
          setState({ loading: false, error: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [previewId, kind]);

  return state;
};

export default usePreviewQuota;
